import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  googleProvider,
  setAuthTenant,
  sendEmailVerification,
} from './firebase';

// Create Auth Context
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantType, setTenantType] = useState(() => {
    // Initialize tenant type from localStorage
    return localStorage.getItem('tenantType');
  });
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);

      if (!user) {
        // If user is null (logged out), clear tenant type
        setTenantType(null);
        localStorage.removeItem('tenantType');
        setLoading(false);
      } else {
        // User is logged in
        console.log('Auth state changed - user logged in');

        // Force tenant type to employee
        const activeTenantType = 'employee';

        // Ensure tenant is set in Firebase
        if (!tenantType || tenantType !== activeTenantType) {
          console.log('Setting tenant type to:', activeTenantType);
          setTenant(activeTenantType);
        }

        // Only navigate if we're on auth pages
        const currentPath = window.location.pathname;
        const isOnAuthPage = currentPath === '/auth/login' || currentPath === '/' || currentPath === '/auth/signup' || currentPath.startsWith('/auth/');

        if (isOnAuthPage) {
          console.log('Navigating to tenant dashboard:', activeTenantType);
          const targetRoute = '/employee';

          // Check if user just signed up
          const fromSignup = localStorage.getItem('fromSignup') === 'true';
          if (fromSignup) {
            localStorage.removeItem('fromSignup');
            navigate('/employee/onboarding', { state: { fromSignup: true }, replace: true });
          } else {
            navigate(targetRoute, { replace: true });
          }
        }

        setLoading(false);
      }
    });

    return unsubscribe;
  }, []); // Remove tenantType from dependencies to prevent re-runs

  const setTenant = (tenantTypeParam) => {
    // Always use employee
    const effectiveTenant = 'employee';
    console.log('Setting tenant:', effectiveTenant);
    let tenantId = process.env.REACT_APP_TENANT_EMPLOYEE;

    // Update state and localStorage
    setTenantType(effectiveTenant);
    localStorage.setItem('tenantType', effectiveTenant);

    console.log('Setting Firebase tenant ID:', tenantId);
    setAuthTenant(tenantId);
  };

  const login = async (email, password, tenantTypeParam) => {
    console.log('Login initiated for tenant:', tenantTypeParam);

    // Set tenant BEFORE attempting login
    setTenant(tenantTypeParam);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Login successful');
      // Navigation will be handled by onAuthStateChanged
      return userCredential;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const loginWithGoogle = async (tenantTypeParam) => {
    console.log('Google login initiated for tenant:', tenantTypeParam);

    // Set tenant BEFORE attempting login
    setTenant(tenantTypeParam);

    try {
      const userCredential = await signInWithPopup(auth, googleProvider);

      // Google accounts are typically pre-verified, but send verification if needed
      if (!userCredential.user.emailVerified) {
        await sendEmailVerification(userCredential.user);
      }

      console.log('Google login successful');
      // Navigation will be handled by onAuthStateChanged
      return userCredential;
    } catch (error) {
      console.error('Google login failed:', error);
      throw error;
    }
  };

  const signup = async (email, password, tenantTypeParam) => {
    console.log('Signup initiated for tenant:', tenantTypeParam);

    // Set tenant BEFORE attempting signup
    setTenant(tenantTypeParam);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Send email verification immediately after signup
      await sendEmailVerification(userCredential.user);

      console.log('Signup successful');
      // Navigation will be handled by onAuthStateChanged or email verification screen
      return userCredential;
    } catch (error) {
      console.error('Signup failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    console.log('Logout initiated');

    // Clear tenant type from localStorage before signing out
    setTenantType(null);
    localStorage.removeItem('tenantType');

    try {
      await signOut(auth);
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    tenantType,
    login,
    loginWithGoogle,
    signup,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

